import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import saccoShareService from "@saccoShareService";

const QUERY_KEY = 'sacco_shares';

export function useFetchSaccoShares(membershipId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [QUERY_KEY, membershipId],
    queryFn: async () => {
      return await saccoShareService.fetchByMembership(user!.id, membershipId);
    },
    enabled: !!user && !!membershipId,
  });
}

/**
 * Hook to create a sacco share.
 * @deprecated use useCreateSaccoShareWithDebit instead
 */
export function useCreateShare(membershipId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: SaccoShareCreateRequest) => {
      return await saccoShareService.create(user!.id, membershipId, input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] }),
  });
}

/**
 * Hook to update a sacco share.
 * @param {string} membershipId The membership ID to lookup
 */
export function useUpdateSaccoShare(membershipId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (updateRequest: SaccoShareUpdateRequest) => {
      return await saccoShareService.update(user!.id, updateRequest);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useDeleteSaccoShare(membershipId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      return await saccoShareService.delete(id, user!.id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
